import axios from "axios";
import { baseURL, handleDecrypt } from "./helper";
import Cookies from "js-cookie";
import store from "../../store";

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunk size
const CONCURRENCY = 3; // Upload up to 3 chunks in parallel for maximum speed

/**
 * Uploads a file in chunks to the backend, which merges and streams directly to AWS S3.
 * @param {Object} params
 * @param {File|Blob} params.file The file to upload
 * @param {Function} [params.onProgress] Callback receiving overall upload percentage (0 - 100) and status string
 * @returns {Promise<string>} The uploaded AWS S3 URL
 */
export const uploadFileInChunks = async ({
  file,
  onProgress = () => {},
}) => {
  if (!file) throw new Error("No file provided for upload");

  const totalSize = file.size;
  const fileName = file.name || "document.pdf";
  const mimetype = file.type || "application/pdf";
  const totalChunks = Math.ceil(totalSize / CHUNK_SIZE) || 1;
  const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

  // Auth Header Handling
  const token = Cookies.get("_xpdx");
  const reduxState = store.getState();
  const reduxToken = reduxState?.authReducer?.accessToken;
  const rawToken = token || reduxToken;
  let authHeader = {};

  if (rawToken) {
    try {
      let decryptedToken = handleDecrypt(rawToken);
      if (!decryptedToken && typeof rawToken === "string" && rawToken.startsWith("eyJ")) {
        decryptedToken = rawToken;
      }
      if (decryptedToken) {
        authHeader = { Authorization: `Bearer ${decryptedToken}` };
      }
    } catch (e) {
      if (typeof rawToken === "string" && rawToken.startsWith("eyJ")) {
        authHeader = { Authorization: `Bearer ${rawToken}` };
      }
    }
  }

  onProgress(2, `Preparing upload (0/${totalChunks} chunks)...`);

  // Track progress of each individual chunk in bytes
  const chunkLoadedBytes = new Array(totalChunks).fill(0);

  const updateOverallProgress = (statusText) => {
    const totalLoaded = chunkLoadedBytes.reduce((acc, bytes) => acc + bytes, 0);
    const percent = Math.min(90, Math.max(2, Math.round((totalLoaded / totalSize) * 90)));
    onProgress(percent, statusText);
  };

  // Helper to upload a single chunk with retry
  const uploadSingleChunk = async (chunkIndex) => {
    const start = chunkIndex * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, totalSize);
    const chunkBlob = file.slice(start, end);
    const chunkSize = end - start;

    const formData = new FormData();
    formData.append("chunk", chunkBlob, fileName);
    formData.append("uploadId", uploadId);
    formData.append("chunkIndex", String(chunkIndex));
    formData.append("totalChunks", String(totalChunks));
    formData.append("fileName", fileName);

    let attempts = 0;
    const maxAttempts = 3;

    while (attempts < maxAttempts) {
      try {
        attempts++;
        await axios.post(baseURL("media/chunk"), formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            ...authHeader,
          },
          onUploadProgress: (progressEvent) => {
            chunkLoadedBytes[chunkIndex] = Math.min(progressEvent.loaded, chunkSize);
            updateOverallProgress(`Uploading chunks (${Math.round((chunkIndex + 1) / totalChunks * 100)}%)...`);
          },
        });

        chunkLoadedBytes[chunkIndex] = chunkSize;
        updateOverallProgress();
        return;
      } catch (err) {
        if (attempts >= maxAttempts) {
          console.error(`Chunk ${chunkIndex + 1}/${totalChunks} failed after ${maxAttempts} attempts:`, err);
          throw new Error(
            err?.response?.data?.message?.error?.[0] ||
            err?.response?.data?.message ||
            err?.message ||
            `Failed to upload chunk ${chunkIndex + 1}`
          );
        }
        // Small delay before retry
        await new Promise((res) => setTimeout(res, 1000));
      }
    }
  };

  // Pool workers to run chunks concurrently up to CONCURRENCY
  const chunkIndices = Array.from({ length: totalChunks }, (_, i) => i);
  const workers = Array(Math.min(CONCURRENCY, totalChunks))
    .fill(null)
    .map(async () => {
      while (chunkIndices.length > 0) {
        const nextIndex = chunkIndices.shift();
        if (nextIndex !== undefined) {
          await uploadSingleChunk(nextIndex);
        }
      }
    });

  await Promise.all(workers);

  // 2. All chunks uploaded to server temp storage, now request merge and stream to AWS S3
  onProgress(92, "Assembling and uploading to AWS S3...");

  let mergeResponse;
  try {
    mergeResponse = await axios.post(
      baseURL("media/merge-chunks"),
      {
        uploadId,
        fileName,
        totalChunks,
        mimetype,
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeader,
        },
        timeout: 300_000, // 5 min timeout for very large files
      }
    );
  } catch (mergeErr) {
    console.error("Merge and S3 upload failed:", mergeErr);
    throw new Error(
      mergeErr?.response?.data?.message?.error?.[0] ||
      mergeErr?.response?.data?.message ||
      mergeErr?.message ||
      "Failed to process and upload file to AWS S3"
    );
  }

  const resultUrl =
    mergeResponse?.data?.data?.url ||
    mergeResponse?.data?.data?.key ||
    mergeResponse?.data?.url;

  if (!resultUrl) {
    throw new Error("Merge succeeded but no URL was returned by the server");
  }

  onProgress(100, "Upload complete!");
  return resultUrl;
};
